import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Typography,
  Link as MuiLink,
  Box,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getEvent } from '../../features/event/eventSlice';
import {
  getGameByEventId,
  moveGameLocally,
  updateGameGreenRink
} from '../../features/game/gameSlice';
import GameMiniCard from '../ui/gameStatusCards/gameByStatusMiniCard';
import { motion } from 'framer-motion';
import { getGreenPlayerInformation } from '../../features/green/greenSlice';
import { GameStatus } from '../../constants/gameConstants';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  buildGreenSwapSuggestions,
  getGreenSuggestionRound
} from './greenSwapSuggestions';

const RINK_KEY_SEPARATOR = '::';

const GREEN_OVERUSE_STORAGE_KEY = 'greenOrganiserOveruseLimit';
const DEFAULT_GREEN_OVERUSE_LIMIT = 60;
const GREEN_OVERUSE_ORANGE_MARGIN = 10;

const buildGreenUsageKey = (venueItem, greenItem) =>
  `${venueItem?.venueId || venueItem?.venueName || ''}${RINK_KEY_SEPARATOR}${greenItem?.greenName || ''}`;

const formatGreenColumnLabel = (greenName) => {
  const name = String(greenName || '').trim();
  if (!name) {
    return 'Green';
  }

  return /^green\b/i.test(name) ? name : `Green ${name}`;
};

const readStoredGreenOveruseLimit = () => {
  try {
    const stored = Number(window.localStorage.getItem(GREEN_OVERUSE_STORAGE_KEY));
    if (Number.isFinite(stored) && stored >= 1 && stored <= 100) {
      return String(Math.round(stored));
    }
  } catch {
    // Storage can be unavailable (private mode); fall back to the default.
  }

  return String(DEFAULT_GREEN_OVERUSE_LIMIT);
};

const buildRinkKey = (venueId, greenName, rinkNumber) =>
  `${venueId}${RINK_KEY_SEPARATOR}${greenName}${RINK_KEY_SEPARATOR}${Number(rinkNumber)}`;

const parseRinkKey = (rinkKey) => {
  const [venueId, greenName, rinkNumber] = String(rinkKey || '').split(RINK_KEY_SEPARATOR);
  return {
    venueId: venueId || '',
    greenName: greenName || '',
    rinkNumber: Number(rinkNumber) || 0
  };
};

const getClientPointFromEvent = (event) => {
  if (!event) {
    return null;
  }

  if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
    return { x: event.clientX, y: event.clientY };
  }

  const touch =
    (event.touches && event.touches[0]) ||
    (event.changedTouches && event.changedTouches[0]) ||
    null;

  if (touch && typeof touch.clientX === 'number' && typeof touch.clientY === 'number') {
    return { x: touch.clientX, y: touch.clientY };
  }

  return null;
};

const RinkBox = ({
  green,
  rink,
  location,
  games,
  onGameDrop,
  isHovered,
  onGameDrag,
  showAllHistory,
  isLoading,
  onGameDragStart,
  onGameDragEnd,
  isAnyDragActive,
  dragPreviewText
}) => {
  const cardRef = React.useRef(null);
  const hasGames = games.length > 0;
  const statusLabel = !rink.active ? 'Inactive' : hasGames ? 'In use' : 'Available';
  const statusColor = !rink.active ? 'error' : hasGames ? 'warning' : 'success';

  return (
    <Card
      variant="outlined"
      sx={{
        width: { xs: '100%', sm: 280 },
        minHeight: 190,
        position: 'relative',
        overflow: 'visible',
        borderWidth: isHovered ? 2 : 1,
        borderStyle: isHovered ? 'dashed' : 'solid',
        borderColor: isHovered ? 'info.main' : `${statusColor}.main`,
        opacity: isLoading ? 0.6 : 1,
        boxShadow: isHovered ? 4 : 0,
        transition: 'all 0.2s ease'
      }}>
      {/* Individual rink loading indicator */}
      {isLoading && (
        <Box
          position="absolute"
          top="50%"
          left="50%"
          sx={{ transform: 'translate(-50%, -50%)', zIndex: 1001 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={700}>
            {green}
            {rink.rink}
          </Typography>
          <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" />
        </Stack>

        {games.length > 0 ? (
          <Stack spacing={1} width="100%" alignItems="center">
            {isAnyDragActive && isHovered ? (
              <Chip
                icon={<DragIndicatorIcon />}
                label={dragPreviewText || 'Drop game here'}
                size="small"
                color="info"
                sx={{ mb: 0.5 }}
              />
            ) : null}
            <Stack spacing={1} width="100%" alignItems="center">
              {games.map((game) => (
                <motion.div
                  key={game.id}
                  drag
                  dragSnapToOrigin
                  style={{ cursor: 'grab', zIndex: 1000, position: 'relative' }}
                  whileDrag={{ zIndex: 9999, scale: 1.02 }}
                  ref={cardRef}
                  onDragStart={() => onGameDragStart?.(game)}
                  onDrag={(event, info) => onGameDrag?.(event, info?.point)}
                  onDragEnd={(event, info) => {
                    if (onGameDrop) {
                      onGameDrop(game, event, info.point);
                    }

                    onGameDragEnd?.();
                  }}>
                  <GameMiniCard game={game} />
                </motion.div>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Box mt={1} textAlign="center" minHeight={56} display="flex" alignItems="center" justifyContent="center">
            {rink.active ? (
              <Typography color={'success.main'} fontSize={13}>
                No game assigned
              </Typography>
            ) : (
              <Typography color={'error.main'} fontSize={13}>
                Rink inactive
              </Typography>
            )}
          </Box>
        )}

        <GreenTeamInformation
          venueId={location.id}
          green={green}
          rink={rink.rink}
          currentGame={games[0] || null}
          showAllHistory={showAllHistory}
        />
      </CardContent>
    </Card>
  );
};

const GreenOrganiser = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [hoveredRinkKey, setHoveredRinkKey] = useState(null);
  const [selectedRound, setSelectedRound] = useState('all');
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [avoidRepeatedEndRinks, setAvoidRepeatedEndRinks] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingRinkKey, setUpdatingRinkKey] = useState(null);
  const [draggingGameId, setDraggingGameId] = useState('');
  const [draggingFromRinkKey, setDraggingFromRinkKey] = useState('');
  const [greenSuggestions, setGreenSuggestions] = useState([]);
  const [remainingGreenIssues, setRemainingGreenIssues] = useState([]);
  const [suggestionRound, setSuggestionRound] = useState(0);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [overuseLimitInput, setOveruseLimitInput] = useState(readStoredGreenOveruseLimit);

  const overuseLimit = useMemo(() => {
    const parsed = Number(overuseLimitInput);
    if (!Number.isFinite(parsed)) {
      return DEFAULT_GREEN_OVERUSE_LIMIT;
    }

    return Math.min(100, Math.max(1, Math.round(parsed)));
  }, [overuseLimitInput]);
  const orangeLimit = Math.max(0, overuseLimit - GREEN_OVERUSE_ORANGE_MARGIN);

  const dispatch = useDispatch();
  const event = useSelector((state) => state.event.event);
  const games = useSelector((state) => state.game.games);
  const gameLoading = useSelector((state) => state.game.loading);
  const greenLoading = useSelector((state) => state.green.isLoading);
  const greenPlayerInformation = useSelector((state) => state.green.greenPlayerInformation);

  const boxRef = useRef(null);
  const rinkRefs = useRef({});

  useEffect(() => {
    if (!event) {
      dispatch(getEvent(eventId));
    }
  }, [dispatch, eventId, event]);

  useEffect(() => {
    if (event && games.length === 0) {
      dispatch(getGameByEventId(event.id));
      dispatch(getGreenPlayerInformation(event.id));
    }
  }, [dispatch, event]);

  useEffect(() => {
    try {
      window.localStorage.setItem(GREEN_OVERUSE_STORAGE_KEY, String(overuseLimit));
    } catch {
      // Storage can be unavailable (private mode); the setting still applies this session.
    }
  }, [overuseLimit]);

  const getGamesOnGreenRink = (green, rink, venue) => {
    let filteredGames = games.filter(
      (game) =>
        game.venue.id === venue.id &&
        game.green === green &&
        game.rink === rink &&
        game.status !== GameStatus.COMPLETE
    );

    // Apply round filter
    if (selectedRound !== 'all') {
      filteredGames = filteredGames.filter((game) => game.round === parseInt(selectedRound));
    }

    return filteredGames.sort((a, b) => {
      const aRound = Number(a.round) || 0;
      const bRound = Number(b.round) || 0;
      if (aRound !== bRound) return aRound - bRound;

      const aSectionNum = Number(a.section);
      const bSectionNum = Number(b.section);
      const aSectionIsNum = Number.isFinite(aSectionNum);
      const bSectionIsNum = Number.isFinite(bSectionNum);

      if (aSectionIsNum && bSectionIsNum && aSectionNum !== bSectionNum) {
        return aSectionNum - bSectionNum;
      }

      const sectionCompare = String(a.section || '').localeCompare(
        String(b.section || ''),
        undefined,
        {
          numeric: true,
          sensitivity: 'base'
        }
      );
      if (sectionCompare !== 0) return sectionCompare;

      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const handleRoundChange = (event) => {
    setSelectedRound(event.target.value);
  };

  const handleShowAllHistoryChange = (event) => {
    setShowAllHistory(event.target.checked);
  };

  const handleResetFilters = () => {
    setSelectedRound('all');
    setShowAllHistory(false);
    setAvoidRepeatedEndRinks(true);
  };

  const showMessage = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const findRinkAtPoint = (point) => {
    if (!point) {
      return null;
    }

    return (
      Object.entries(rinkRefs.current).find(([key, ref]) => {
        if (!ref?.current) return false;
        const rect = ref.current.getBoundingClientRect();
        return (
          point.x >= rect.left &&
          point.x <= rect.right &&
          point.y >= rect.top &&
          point.y <= rect.bottom
        );
      }) || null
    );
  };

  const handleGameDrop = async (game, dragEvent, point) => {
    const finalPoint = getClientPointFromEvent(dragEvent) || point;
    const target = findRinkAtPoint(finalPoint);

    if (!target) {
      showMessage('Drop over a valid rink to move or swap games.');
      setHoveredRinkKey(null);
      return;
    }

    if (target) {
      const [targetKey] = target;
      const { venueId: locationId, greenName, rinkNumber } = parseRinkKey(targetKey);

      const sourceKey = buildRinkKey(game?.venue?.id, game?.green, game?.rink);
      if (sourceKey === targetKey) {
        setHoveredRinkKey(null);
        return;
      }

      const location = (event?.greens || []).find((loc) => loc.id === locationId);
      if (!location) {
        showMessage('Could not resolve target venue for drop.');
        setHoveredRinkKey(null);
        return;
      }

      const green = (location.greens || []).find((g) => g.name === greenName);
      if (!green) {
        showMessage('Could not resolve target green for drop.');
        setHoveredRinkKey(null);
        return;
      }

      const rink = (green.rinks || []).find((r) => r.rink === Number(rinkNumber));
      if (!rink) {
        showMessage('Could not resolve target rink for drop.');
        setHoveredRinkKey(null);
        return;
      }

      if (!rink.active) {
        showMessage('Cannot drop onto an inactive rink.');
        setHoveredRinkKey(null);
        return;
      }

      // Set loading state
      setIsUpdating(true);
      setUpdatingRinkKey(targetKey);

      try {
        const targetGames = getGamesOnGreenRink(greenName, Number(rinkNumber), { id: locationId });
        const targetGame = targetGames.find((g) => g.id !== game.id) || targetGames[0];
        const targetGameId = targetGame ? targetGame.id : null;

        // Update local state first
        dispatch(
          moveGameLocally({
            gameId: game.id,
            green: greenName,
            rink: Number(rinkNumber),
            venueId: locationId,
            targetGameId
          })
        );

        // Update on server
        await dispatch(
          updateGameGreenRink({
            gameId: game.id,
            green: greenName,
            rink: Number(rinkNumber),
            venueId: locationId,
            targetGameId
          })
        ).unwrap();

        // Refetch data after successful update
        await Promise.all([
          dispatch(getGameByEventId(event.id)),
          dispatch(getGreenPlayerInformation(event.id))
        ]);
      } catch (error) {
        console.error('Error updating game:', error);
        showMessage('Failed to update game assignment.');
      } finally {
        setIsUpdating(false);
        setUpdatingRinkKey(null);
      }
    }

    setHoveredRinkKey(null);
  };

  const handlePointerMove = (event, point) => {
    if (!draggingGameId) {
      return;
    }

    const finalPoint = getClientPointFromEvent(event) || point;
    const target = findRinkAtPoint(finalPoint);
    setHoveredRinkKey(target ? target[0] : null);
  };

  const handleGameDragStart = (game) => {
    if (!game?.id) {
      return;
    }

    setDraggingGameId(game.id);
    setDraggingFromRinkKey(buildRinkKey(game?.venue?.id, game?.green, game?.rink));
  };

  const handleGameDragEnd = () => {
    setDraggingGameId('');
    setDraggingFromRinkKey('');
    setHoveredRinkKey(null);
  };

  const refreshGreenSuggestions = (showToast = false) => {
    if (!event || !Array.isArray(games) || games.length === 0) {
      setGreenSuggestions([]);
      setRemainingGreenIssues([]);
      setSuggestionRound(0);
      return;
    }

    const result = buildGreenSwapSuggestions({
      event,
      games,
      selectedRound,
      avoidTeamsOverusedOnEndRinks: avoidRepeatedEndRinks
    });
    setGreenSuggestions(result.suggestions || []);
    setRemainingGreenIssues(result.remainingIssues || []);
    setSuggestionRound(result.round || getGreenSuggestionRound(event, games, selectedRound));

    if (showToast) {
      if (!result.suggestions?.length) {
        showMessage('No green swap suggestions found for the selected round.');
      } else {
        showMessage(`${result.suggestions.length} green swap suggestions prepared.`);
      }
    }
  };

  useEffect(() => {
    refreshGreenSuggestions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, games, selectedRound, avoidRepeatedEndRinks]);

  const applyGreenSuggestion = async (suggestion, silent = false) => {
    if (!suggestion?.sourceGameId || !suggestion?.to?.venueId) {
      return null;
    }

    const sourceGame = games.find((g) => g.id === suggestion.sourceGameId);
    if (!sourceGame) {
      if (!silent) {
        showMessage('Could not find source game to apply this suggestion.');
      }
      return null;
    }

    const targetGameId = suggestion.targetGameId || null;
    const targetKey = buildRinkKey(suggestion.to.venueId, suggestion.to.green, suggestion.to.rink);

    setIsUpdating(true);
    setUpdatingRinkKey(targetKey);

    try {
      dispatch(
        moveGameLocally({
          gameId: sourceGame.id,
          green: suggestion.to.green,
          rink: Number(suggestion.to.rink),
          venueId: suggestion.to.venueId,
          targetGameId
        })
      );

      await dispatch(
        updateGameGreenRink({
          gameId: sourceGame.id,
          green: suggestion.to.green,
          rink: Number(suggestion.to.rink),
          venueId: suggestion.to.venueId,
          targetGameId
        })
      ).unwrap();

      const refreshedGames = await dispatch(getGameByEventId(event.id)).unwrap();
      await dispatch(getGreenPlayerInformation(event.id));

      if (!silent) {
        showMessage(
          suggestion.type === 'swap'
            ? 'Suggestion applied: games swapped between rinks.'
            : 'Suggestion applied: game moved to suggested rink.'
        );
      }

      return refreshedGames;
    } catch (error) {
      if (!silent) {
        showMessage('Failed to apply green suggestion.');
      }
      return null;
    } finally {
      setIsUpdating(false);
      setUpdatingRinkKey(null);
    }
  };

  const applyAllGreenSuggestions = async () => {
    if (!greenSuggestions.length) {
      showMessage('No suggestions available to apply.');
      return;
    }

    setApplyingSuggestions(true);
    let latestGames = games;
    let applied = 0;

    try {
      const maxIterations = Math.max(greenSuggestions.length * 2, 1);

      for (let i = 0; i < maxIterations; i += 1) {
        const refreshed = buildGreenSwapSuggestions({
          event,
          games: latestGames,
          selectedRound,
          avoidTeamsOverusedOnEndRinks: avoidRepeatedEndRinks
        });

        if (!refreshed.suggestions.length) {
          break;
        }

        const nextSuggestion = refreshed.suggestions[0];
        const updatedGames = await applyGreenSuggestion(nextSuggestion, true);
        if (!updatedGames) {
          break;
        }

        latestGames = updatedGames;
        applied += 1;
      }

      showMessage(`Applied ${applied} green suggestion${applied === 1 ? '' : 's'}.`);
    } finally {
      setApplyingSuggestions(false);
    }
  };

  // Get available rounds from event
  const availableRounds = event?.gameFormat?.rounds || 0;

  const rinkStats = useMemo(() => {
    if (!Array.isArray(event?.greens)) {
      return { total: 0, active: 0, occupied: 0, available: 0 };
    }

    const selectedRoundNumber = selectedRound === 'all' ? null : Number(selectedRound);
    let total = 0;
    let active = 0;
    let occupied = 0;

    event.greens.forEach((location) => {
      (location.greens || []).forEach((green) => {
        (green.rinks || []).forEach((rink) => {
          total += 1;
          if (!rink.active) {
            return;
          }

          active += 1;

          const hasGame = games.some((game) => {
            if (game.status === GameStatus.COMPLETE) {
              return false;
            }

            if (selectedRoundNumber !== null && Number(game.round) !== selectedRoundNumber) {
              return false;
            }

            return (
              game.venue.id === location.id &&
              game.green === green.name &&
              Number(game.rink) === Number(rink.rink)
            );
          });

          if (hasGame) {
            occupied += 1;
          }
        });
      });
    });

    return {
      total,
      active,
      occupied,
      available: Math.max(active - occupied, 0)
    };
  }, [event?.greens, games, selectedRound]);

  const teamRinkUsageRows = useMemo(() => {
    if (!Array.isArray(greenPlayerInformation)) {
      return [];
    }

    const usageByTeamId = new Map();

    greenPlayerInformation.forEach((venueItem) => {
      (venueItem.greens || []).forEach((greenItem) => {
        const greenKey = buildGreenUsageKey(venueItem, greenItem);

        (greenItem.rinks || []).forEach((rinkItem) => {
          const rinkNumber = Number(rinkItem.rinkNumber || 0);
          const bucket =
            rinkNumber === 1 || rinkNumber === 6
              ? 'endCount'
              : rinkNumber >= 2 && rinkNumber <= 5
                ? 'middleCount'
                : null;

          (rinkItem.teams || []).forEach((teamItem) => {
            if (!teamItem?.teamId) {
              return;
            }

            const existing = usageByTeamId.get(teamItem.teamId) || {
              teamId: teamItem.teamId,
              teamName: teamItem.teamName || 'Unknown team',
              endCount: 0,
              middleCount: 0,
              totalCount: 0,
              greenCounts: {}
            };

            const gamesPlayed = Number(teamItem.totalGames || 0);
            if (bucket) {
              existing[bucket] += gamesPlayed;
            }

            existing.totalCount += gamesPlayed;
            existing.greenCounts[greenKey] = (existing.greenCounts[greenKey] || 0) + gamesPlayed;
            usageByTeamId.set(teamItem.teamId, existing);
          });
        });
      });
    });

    const activeGames = Array.isArray(games)
      ? games.filter((game) => Number(game?.status) !== GameStatus.COMPLETE)
      : [];

    const selectedRoundNumber = selectedRound === 'all' ? null : Number(selectedRound);
    const latestRound = activeGames.length
      ? Math.max(...activeGames.map((game) => Number(game?.round || 0)))
      : 0;
    const assignmentRound = selectedRoundNumber || latestRound;

    const assignmentByTeamId = new Map();
    activeGames
      .filter((game) => Number(game?.round || 0) === assignmentRound)
      .forEach((game) => {
        const fallbackVenueName = (event?.greens || []).find((v) => v.id === game?.venue?.id)?.name || 'Venue';
        const venueName = game?.venue?.name || fallbackVenueName;
        const rinkNumber = Number(game?.rink || 0);
        const label = `${venueName} ${game?.green || ''}${rinkNumber || ''}`.trim();
        const isEndRink = rinkNumber === 1 || rinkNumber === 6;

        [game?.teams?.teamA, game?.teams?.teamB].forEach((team) => {
          const teamId = team?.id;
          if (!teamId) {
            return;
          }

          const existing = assignmentByTeamId.get(teamId) || {
            labels: [],
            currentlyOnEndRink: false
          };

          if (!existing.labels.includes(label)) {
            existing.labels.push(label);
          }

          if (isEndRink) {
            existing.currentlyOnEndRink = true;
          }

          assignmentByTeamId.set(teamId, existing);
        });
      });

    return Array.from(usageByTeamId.values())
      .map((row) => ({
        ...row,
        currentAssignment: (assignmentByTeamId.get(row.teamId)?.labels || []).join(', ') || 'Not assigned',
        currentlyOnEndRink: Boolean(assignmentByTeamId.get(row.teamId)?.currentlyOnEndRink)
      }))
      .sort((a, b) => {
        if (b.totalCount !== a.totalCount) {
          return b.totalCount - a.totalCount;
        }
        return String(a.teamName || '').localeCompare(String(b.teamName || ''), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
  }, [greenPlayerInformation, games, selectedRound, event?.greens]);

  const greenUsageColumns = useMemo(() => {
    if (!Array.isArray(greenPlayerInformation)) {
      return [];
    }

    const seen = new Set();
    const columns = [];

    greenPlayerInformation.forEach((venueItem) => {
      (venueItem.greens || []).forEach((greenItem) => {
        const key = buildGreenUsageKey(venueItem, greenItem);
        if (seen.has(key)) {
          return;
        }

        // Greens nobody has played on would only add empty columns.
        const hasUsage = (greenItem.rinks || []).some((rinkItem) =>
          (rinkItem.teams || []).some((teamItem) => Boolean(teamItem?.teamId))
        );
        if (!hasUsage) {
          return;
        }

        seen.add(key);
        columns.push({
          key,
          greenName: greenItem.greenName,
          venueName: venueItem.venueName || ''
        });
      });
    });

    return columns;
  }, [greenPlayerInformation]);

  const hasMultipleGreenVenues = useMemo(
    () => new Set(greenUsageColumns.map((column) => column.venueName)).size > 1,
    [greenUsageColumns]
  );

  // Overall loading state (for backdrop)
  const isOverallLoading = gameLoading || greenLoading || isUpdating;

  return (
    event && (
      <Box mt={2} mb={2} ref={boxRef} position="relative">
        {/* Global Loading Backdrop - Dark Theme */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            position: 'absolute',
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }}
          open={isOverallLoading}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress color="inherit" />
            <Typography variant="body2" color="inherit">
              {applyingSuggestions
                ? 'Applying suggested rink moves...'
                : isUpdating
                  ? 'Updating game assignment...'
                  : 'Loading data...'}
            </Typography>
          </Box>
        </Backdrop>

        <Stack spacing={2} mb={2}>
          <Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/events/${eventId}`)}>
              Back to Event
            </Button>
          </Box>

          <Box>
            <MuiLink component={Link} to={`/events/${event.id}`} underline="hover">
              <Typography variant="h5" fontWeight={700}>
                {event.name} - Green Opponents
              </Typography>
            </MuiLink>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Drag game cards between rinks to reassign. Click filters to focus rounds and history.
            </Typography>
          </Box>

          <Card variant="outlined" sx={{ position: 'sticky', top: 8, zIndex: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip size="small" color="info" label={`Total rinks: ${rinkStats.total}`} />
                  <Chip size="small" color="success" label={`Available: ${rinkStats.available}`} />
                  <Chip size="small" color="warning" label={`In use: ${rinkStats.occupied}`} />
                  <Chip size="small" color="default" label={`Active: ${rinkStats.active}`} />
                </Stack>

                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ ml: { md: 'auto' } }}>
                  <FormControl size="small" sx={{ minWidth: 140 }} disabled={isUpdating}>
                    <InputLabel>Round</InputLabel>
                    <Select value={selectedRound} onChange={handleRoundChange} label="Round">
                      <MenuItem value="all">All Rounds</MenuItem>
                      {Array.from({ length: availableRounds }, (_, i) => i + 1).map((round) => (
                        <MenuItem key={round} value={round.toString()}>
                          Round {round}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showAllHistory}
                        onChange={handleShowAllHistoryChange}
                        size="small"
                        disabled={isUpdating}
                      />
                    }
                    label={<Typography variant="body2">Show All History</Typography>}
                  />

                  <Tooltip title="Reset round filter and history toggle">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetFilters}
                        disabled={isUpdating}>
                        Reset
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Name Color Legend:
                </Typography>
                <Chip size="small" color="success" label="Green name = 1 time played on rink" />
                <Chip size="small" color="warning" label="Orange name = 2 times played on rink" />
                <Chip size="small" color="error" label="Red name = 3+ times played on rink" />
                <Chip size="small" label="Hover team names for End/Middle totals" variant="outlined" />
              </Stack>

              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1.25 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={avoidRepeatedEndRinks}
                      onChange={(event) => setAvoidRepeatedEndRinks(event.target.checked)}
                      size="small"
                      disabled={isUpdating || applyingSuggestions}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Suggestions: avoid end rinks for teams with more than 2 prior end-rink games
                    </Typography>
                  }
                />
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Stack direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Suggestions check Round {suggestionRound || getGreenSuggestionRound(event, games, selectedRound)}
                </Typography>
                <Stack direction="row" gap={1} sx={{ ml: { md: 'auto' } }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<AutoFixHighIcon />}
                    onClick={() => refreshGreenSuggestions(true)}
                    disabled={isUpdating || applyingSuggestions}>
                    Regenerate Suggestions
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<AutoFixHighIcon />}
                    onClick={applyAllGreenSuggestions}
                    disabled={!greenSuggestions.length || isUpdating || applyingSuggestions}>
                    Apply All ({greenSuggestions.length})
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

        </Stack>

        {greenSuggestions.length > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
              Suggested rink moves for Round {suggestionRound}:
            </Typography>
            <Stack spacing={1}>
              {greenSuggestions.map((suggestion, index) => (
                <Box
                  key={suggestion.id || `${suggestion.sourceGameId}-${index}`}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap'
                  }}>
                  <Box sx={{ minWidth: 280, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {index + 1}. {suggestion.type === 'swap' ? 'Swap' : 'Move'} {suggestion.sourceGameName}{' '}
                      from {suggestion.from.green}
                      {suggestion.from.rink} to {suggestion.to.green}
                      {suggestion.to.rink}
                      {suggestion.type === 'swap' && suggestion.targetGameName
                        ? ` (with ${suggestion.targetGameName})`
                        : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {suggestion.note}
                    </Typography>
                    {Array.isArray(suggestion.why) && suggestion.why.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Why: {suggestion.why.join(' | ')}
                      </Typography>
                    ) : null}
                    {suggestion.unavoidable ? (
                      <Chip
                        size="small"
                        color="warning"
                        label="Some repeat pressure remains (unavoidable)"
                        sx={{ mt: 0.5 }}
                      />
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isUpdating || applyingSuggestions}
                    onClick={() => applyGreenSuggestion(suggestion)}>
                    Apply
                  </Button>
                </Box>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {remainingGreenIssues.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>
              Remaining unavoidable pressure in Round {suggestionRound}:
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {remainingGreenIssues
                .slice(0, 4)
                .map((issue) => `${issue.gameName} (${issue.green}${issue.rink}): ${issue.reasons.join(' ')}`)
                .join(' | ')}
            </Typography>
          </Alert>
        ) : null}

        {Array.isArray(event.greens) &&
          event.greens.map((location, index) => (
            <Card key={index} variant="outlined" sx={{ mt: 2, mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} mb={1.5}>
                  {index + 1}. {location.name}
                </Typography>
                {location.greens.map((green, gIdx) => (
                  <Box key={gIdx} mb={2}>
                    <Chip size="small" color="primary" variant="outlined" label={`Green ${green.name}`} sx={{ mb: 1 }} />
                    <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1.25}>
                  {green.rinks.map((rink, rIdx) => {
                    const rinkKey = buildRinkKey(location.id, green.name, rink.rink);
                    if (!rinkRefs.current[rinkKey]) rinkRefs.current[rinkKey] = React.createRef();

                    const fromMeta = parseRinkKey(draggingFromRinkKey);
                    const showGhostPreview =
                      Boolean(draggingGameId) &&
                      hoveredRinkKey === rinkKey &&
                      Boolean(draggingFromRinkKey) &&
                      draggingFromRinkKey !== rinkKey;
                    const dragPreviewText = showGhostPreview
                      ? `Drop: ${fromMeta.greenName}${fromMeta.rinkNumber} -> ${green.name}${rink.rink}`
                      : '';

                    return (
                      <Box ref={rinkRefs.current[rinkKey]} key={rinkKey} sx={{ overflow: 'visible' }}>
                        <RinkBox
                          green={green.name}
                          rink={rink}
                          location={location}
                          games={getGamesOnGreenRink(green.name, rink.rink, location)}
                          onGameDrop={handleGameDrop}
                          isHovered={hoveredRinkKey === rinkKey}
                          onGameDrag={handlePointerMove}
                          showAllHistory={showAllHistory}
                          isLoading={updatingRinkKey === rinkKey}
                          onGameDragStart={handleGameDragStart}
                          onGameDragEnd={handleGameDragEnd}
                          isAnyDragActive={Boolean(draggingGameId)}
                          dragPreviewText={dragPreviewText}
                        />
                      </Box>
                    );
                  })}
                    </Box>
                </Box>
              ))}
              </CardContent>
            </Card>
          ))}

        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              gap={1}
              sx={{ mb: 1 }}>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Team Green & Rink Usage (All Greens)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Green columns count every game on that green. Orange from {orangeLimit}% and red above{' '}
                  {overuseLimit}% of a team&apos;s games.
                </Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                label="Red above %"
                value={overuseLimitInput}
                onChange={(event) => setOveruseLimitInput(event.target.value)}
                onBlur={() => setOveruseLimitInput(String(overuseLimit))}
                inputProps={{ min: 1, max: 100, step: 5 }}
                sx={{ width: { xs: '100%', sm: 130 }, flexShrink: 0 }}
              />
            </Stack>
            {teamRinkUsageRows.length > 0 ? (
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader aria-label="team rink usage table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Team</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.75 }}>Assigned Now</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'info.main', py: 0.75 }} align="right">
                        End
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', py: 0.75 }} align="right">
                        Middle
                      </TableCell>
                      {greenUsageColumns.map((column) => (
                        <TableCell
                          key={column.key}
                          align="right"
                          sx={{ fontWeight: 700, py: 0.75, whiteSpace: 'nowrap' }}>
                          {formatGreenColumnLabel(column.greenName)}
                          {hasMultipleGreenVenues && column.venueName ? (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              fontWeight={400}>
                              {column.venueName}
                            </Typography>
                          ) : null}
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, py: 0.75 }} align="right">
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamRinkUsageRows.map((row) => (
                      <TableRow
                        key={row.teamId}
                        sx={
                          row.endCount > 2
                            ? row.currentlyOnEndRink
                              ? { backgroundColor: 'rgba(211,47,47,0.14)' }
                              : { backgroundColor: 'rgba(237,108,2,0.14)' }
                            : undefined
                        }>
                        <TableCell sx={{ py: 0.5 }}>{row.teamName}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography
                            variant="caption"
                            color={row.currentlyOnEndRink ? 'error.main' : 'text.secondary'}
                            sx={row.currentlyOnEndRink ? { fontWeight: 700 } : undefined}>
                            {row.currentAssignment}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            py: 0.5,
                            fontWeight: row.endCount > 2 ? 700 : 500,
                            color: row.currentlyOnEndRink ? 'error.main' : row.endCount > 2 ? 'warning.dark' : 'text.primary'
                          }}>
                          {row.endCount}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>{row.middleCount}</TableCell>
                        {greenUsageColumns.map((column) => {
                          const greenGames = row.greenCounts?.[column.key] || 0;
                          const share = row.totalCount > 0 ? (greenGames / row.totalCount) * 100 : 0;
                          const isOver = greenGames > 0 && share > overuseLimit;
                          const isWarning = greenGames > 0 && !isOver && share >= orangeLimit;
                          const cellColor = isOver
                            ? 'error.main'
                            : isWarning
                              ? 'warning.dark'
                              : 'text.primary';

                          return (
                            <TableCell
                              key={column.key}
                              align="right"
                              sx={{
                                py: 0.5,
                                backgroundColor: isOver
                                  ? 'rgba(211,47,47,0.14)'
                                  : isWarning
                                    ? 'rgba(237,108,2,0.14)'
                                    : undefined
                              }}>
                              <Typography
                                variant="body2"
                                title={`${greenGames} of ${row.totalCount} games (${Math.round(share)}%)`}
                                sx={{
                                  fontWeight: isOver ? 700 : isWarning ? 600 : 500,
                                  color: cellColor
                                }}>
                                {greenGames}
                              </Typography>
                              {greenGames > 0 ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: isOver || isWarning ? cellColor : 'text.secondary' }}>
                                  {Math.round(share)}%
                                </Typography>
                              ) : null}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right" sx={{ py: 0.5 }}>{row.totalCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No team rink history available yet.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    )
  );
};

const GreenTeamInformation = ({ venueId, green, rink, currentGame, showAllHistory }) => {
  const greenInfo = useSelector((state) => state.green.greenPlayerInformation);

  const safeGreenInfo = Array.isArray(greenInfo) ? greenInfo : [];
  const venueData = safeGreenInfo.find((venue) => venue.venueId === venueId);
  const greenData = venueData?.greens?.find((g) => g.greenName === green);
  const greenRinks = greenData?.rinks || [];

  // Find rink data for BOTH directions
  const rinkDataDir1 = greenRinks.find((r) => r.rinkNumber === rink && r.direction === 1);
  const rinkDataDir2 = greenRinks.find((r) => r.rinkNumber === rink && r.direction === 2);

  // Combine teams from both directions
  const allTeams = [];
  if (rinkDataDir1) allTeams.push(...rinkDataDir1.teams);
  if (rinkDataDir2) allTeams.push(...rinkDataDir2.teams);

  // Helper function to get color based on status
  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'success.main'; // Green
      case 'warning':
        return 'warning.main'; // Orange/Yellow
      case 'attention':
        return 'error.main'; // Red
      default:
        return 'text.primary'; // Default text color
    }
  };

  const getStatusColorByPreviousGames = (previousGamesCount) => {
    if (previousGamesCount <= 0) {
      return 'text.secondary';
    }

    if (previousGamesCount === 1) {
      return 'success.main';
    }

    if (previousGamesCount === 2) {
      return 'warning.main';
    }

    return 'error.main';
  };

  const getRinkTypeMarker = (rinkNumber) => {
    const rinkValue = Number(rinkNumber);

    if (rinkValue === 1 || rinkValue === 6) {
      return {
        label: 'E',
        color: 'info.main',
        title: 'End/side rink'
      };
    }

    if (rinkValue >= 2 && rinkValue <= 5) {
      return {
        label: 'M',
        color: '#1b5e20',
        title: 'Middle rink'
      };
    }

    return null;
  };

  const teamRinkTypeCounts = useMemo(() => {
    const countsByTeamId = new Map();

    if (!safeGreenInfo.length) {
      return countsByTeamId;
    }

    safeGreenInfo.forEach((venueItem) => {
      (venueItem.greens || []).forEach((greenItem) => {
        (greenItem.rinks || []).forEach((rinkItem) => {
          const marker = getRinkTypeMarker(rinkItem.rinkNumber);
          if (!marker) {
            return;
          }

          (rinkItem.teams || []).forEach((teamItem) => {
            const teamId = teamItem.teamId;
            if (!teamId) {
              return;
            }

            const existingCounts = countsByTeamId.get(teamId) || { E: 0, M: 0 };
            existingCounts[marker.label] += Number(teamItem.totalGames || 0);
            countsByTeamId.set(teamId, existingCounts);
          });
        });
      });
    });

    return countsByTeamId;
  }, [safeGreenInfo]);

  if (!safeGreenInfo.length) return null;
  if (!venueData) return null;
  if (!greenData) return null;

  const renderTeamNameWithTooltip = (teamId, teamName, color, sx = {}) => {
    const teamCounts = teamRinkTypeCounts.get(teamId) || { E: 0, M: 0 };

    return (
      <Tooltip
        arrow
        title={`End rinks (1 or 6): ${Number(teamCounts.E || 0)} | Middle rinks (2-5): ${Number(teamCounts.M || 0)}`}>
        <Typography component="span" fontSize={9} color={color} sx={sx}>
          {teamName || 'Unknown team'}
        </Typography>
      </Tooltip>
    );
  };

  // If showAllHistory is false, only show current game info
  if (!showAllHistory) {
    if (!currentGame) {
      return (
        <Box mt={2}>
          <Typography fontSize={9} color="text.secondary">
            No current game assigned
          </Typography>
        </Box>
      );
    }

    // Get current team names
    // const currentTeamNames = [
    //   currentGame.teams?.teamA?.name,
    //   currentGame.teams?.teamB?.name
    // ].filter(Boolean);

    // // Find current teams in the history to show their rink usage status
    // const currentTeamsInfo = allTeams.filter((team) => currentTeamNames.includes(team.teamName));

    // Get current team names
    const currentTeamIds = [currentGame.teams?.teamA?.id, currentGame.teams?.teamB?.id].filter(
      Boolean
    );

    // Find current teams in the history to show their rink usage status
    const currentTeamsInfo = allTeams.filter((team) => currentTeamIds.includes(team.teamId));

    const currentTeamsInfoWithPreviousCount = currentTeamsInfo
      .map((team) => {
        const includesCurrentGame = Array.isArray(team.games)
          ? team.games.some((game) => game.gameId === currentGame.id)
          : false;
        const previousGamesCount = Math.max(
          Number(team.totalGames || 0) - (includesCurrentGame ? 1 : 0),
          0
        );

        return {
          ...team,
          previousGamesCount
        };
      })
      .filter((team) => team.previousGamesCount > 0);

    return (
      <Box mt={2}>
        <Typography fontSize={8} color="text.secondary" sx={{ mb: 0.5 }}>
          Current Teams:
        </Typography>
        {currentTeamsInfoWithPreviousCount.length > 0 ? (
          currentTeamsInfoWithPreviousCount.map((team, index) => (
            <Box key={index}>
              <Typography fontSize={9}>
                {renderTeamNameWithTooltip(team.teamId, team.teamName, getStatusColorByPreviousGames(team.previousGamesCount), {
                  fontWeight: 'bold'
                })}
                <Typography component="span" fontSize={9} sx={{ ml: 0.5 }}>
                  ({team.previousGamesCount} previous {team.previousGamesCount === 1 ? 'game' : 'games'} on this rink)
                </Typography>
              </Typography>
            </Box>
          ))
        ) : (
          <Typography fontSize={9}>No previous games on this rink</Typography>
        )}
      </Box>
    );
  }

  // If showAllHistory is true, show complete history (existing logic)
  if (allTeams.length === 0) {
    return (
      <Box mt={2}>
        <Typography fontSize={9}>No games played on this rink.</Typography>
      </Box>
    );
  }

  // Get ALL unique games from ALL teams (both directions)
  const uniqueGames = [];
  const seenGameIds = new Set();

  allTeams.forEach((team) => {
    team.games.forEach((game) => {
      if (!seenGameIds.has(game.gameId)) {
        seenGameIds.add(game.gameId);

        // Find the opponent team's status from all teams
        const opponentTeam = allTeams.find((t) => t.teamName === game.opponentTeamName);
        const opponentStatus = opponentTeam ? opponentTeam.status : null;

        uniqueGames.push({
          ...game,
          teamId: team.teamId,
          teamName: team.teamName,
          teamStatus: team.status,
          opponentStatus: opponentStatus,
          opponentTeamId: game.opponentTeamId,
          isCurrent: currentGame && game.gameId === currentGame.id
        });
      }
    });
  });

  // Sort by round for consistent display, then reverse to show most recent first
  uniqueGames.sort((a, b) => a.round - b.round).reverse();

  // Get current team names for highlighting
  const currentTeamNames = currentGame
    ? [currentGame.teams?.teamA?.name, currentGame.teams?.teamB?.name].filter(Boolean)
    : [];

  return (
    <Box mt={2}>
      <Typography fontSize={8} color="text.secondary" sx={{ mb: 0.5 }}>
        All History ({uniqueGames.length} games):
      </Typography>
      {uniqueGames.map((game) => (
        <Box key={game.gameId}>
          <Typography fontSize={9}>
            R{game.round}:
            {renderTeamNameWithTooltip(game.teamId, game.teamName, getStatusColor(game.teamStatus), {
              ml: 0.5,
              fontWeight: currentTeamNames.includes(game.teamName) ? 'bold' : 'normal'
            })}
            {' vs '}
            {renderTeamNameWithTooltip(
              game.opponentTeamId,
              game.opponentTeamName || 'N/A',
              getStatusColor(game.opponentStatus),
              {
                fontWeight: currentTeamNames.includes(game.opponentTeamName) ? 'bold' : 'normal'
              }
            )}
            {game.greenDirection && (
              <Typography component="span" fontSize={9} sx={{ ml: 0.5 }}>
                [Dir{game.greenDirection}]
              </Typography>
            )}
            {game.isCurrent && (
              <Typography
                component="span"
                fontSize={9}
                color="primary.main"
                sx={{ ml: 0.5, fontWeight: 'bold' }}>
                (Current)
              </Typography>
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default GreenOrganiser;
